import { Button, Card, CardContent, Checkbox, Input, Label, Spinner } from '@crm/ui';
import { Check, Copy, ExternalLink, Globe, ImagePlus, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormError, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { cn, formatCurrency } from '@/lib/utils';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useServices } from '@/modules/catalog/api';
import {
  LANDING_ROUTES,
  type LandingAccent,
  type LandingConfig,
  useLandingConfig,
  useSaveLandingConfig,
} from '../api';

const ACCENTS: { value: LandingAccent; label: string; swatch: string }[] = [
  { value: 'VIOLET', label: 'Violeta', swatch: 'bg-violet-600' },
  { value: 'ROSE', label: 'Rose', swatch: 'bg-rose-600' },
  { value: 'EMERALD', label: 'Esmeralda', swatch: 'bg-emerald-600' },
  { value: 'AMBER', label: 'Ambar', swatch: 'bg-amber-600' },
  { value: 'SLATE', label: 'Grafite', swatch: 'bg-slate-800' },
];

/**
 * Cada banner ganha uma chave so para o React: a lista e reordenavel e o
 * indice nao serve de identidade. A chave nao vai para a API.
 */
interface SlideDraft {
  key: string;
  image: string;
  title: string;
  subtitle: string;
}

type Form = Omit<LandingConfig, 'publicUrl' | 'slides'> & { slides: SlideDraft[] };

/** Configuracao da pagina publica: o que o cliente ve antes de agendar. */
export function LandingConfigPage() {
  useDocumentTitle('Configuracao da landing page');

  const canWrite = useCan('settings', 'WRITE');
  const config = useLandingConfig();
  const save = useSaveLandingConfig();
  const services = useServices({ page: 1, perPage: 100 });

  const [form, setForm] = useState<Form | null>(null);
  const [copied, setCopied] = useState(false);

  // A tela nasce com o que ja esta publicado (ou com o padrao da empresa).
  useEffect(() => {
    if (!config.data || form) return;

    const { publicUrl: _ignored, slides, ...rest } = config.data;
    setForm({ ...rest, slides: slides.map((slide) => ({ ...slide, key: crypto.randomUUID() })) });
  }, [config.data, form]);

  if (config.isLoading || !form) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const publicUrl = `${window.location.origin}${LANDING_ROUTES.publicPage(form.slug)}`;
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  const toggleHighlight = (id: string) =>
    set(
      'highlightIds',
      form.highlightIds.includes(id)
        ? form.highlightIds.filter((item) => item !== id)
        : [...form.highlightIds, id],
    );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Globe />}
        title="Configuracao da landing page"
        description="A pagina que o cliente abre para conhecer o salao e agendar sozinho."
        actions={
          <Button asChild variant="outline">
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4" />
              Ver pagina
            </a>
          </Button>
        }
      />

      <FormError message={save.error ? toErrorMessage(save.error) : null} />

      <Card variant="flush">
        <CardContent className="space-y-6">
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Endereco</h2>

            <div className="space-y-2">
              <Label htmlFor="slug">Endereco da pagina</Label>
              <Input
                id="slug"
                disabled={!canWrite}
                value={form.slug}
                onChange={(event) =>
                  set('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))
                }
              />
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span className="break-all">{publicUrl}</span>
                <Button
                  variant="ghost"
                  className="h-9 px-2 text-sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(publicUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
            </div>

            <Checkbox
              label="Pagina publicada"
              disabled={!canWrite}
              checked={form.isPublished}
              onChange={(event) => set('isPublished', event.target.checked)}
            />
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <h2 className="text-base font-semibold">Aparencia</h2>
            <div className="flex flex-wrap gap-2">
              {ACCENTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={!canWrite}
                  onClick={() => set('accent', option.value)}
                  className={cn(
                    'flex h-11 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                    form.accent === option.value
                      ? 'border-primary bg-primary/10 font-medium'
                      : 'border-border hover:bg-muted',
                  )}
                >
                  <span className={cn('h-4 w-4 rounded-full', option.swatch)} />
                  {option.label}
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-base font-semibold">Conteudo</h2>

            <div className="space-y-2">
              <Label htmlFor="headline">Chamada principal</Label>
              <Input
                id="headline"
                disabled={!canWrite}
                value={form.headline}
                onChange={(event) => set('headline', event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tagline">Linha de apoio</Label>
              <Input
                id="tagline"
                disabled={!canWrite}
                value={form.tagline}
                onChange={(event) => set('tagline', event.target.value)}
              />
            </div>

            {/* Cada banner com a frase dele: foto, chamada e linha de apoio. */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label>Banners do topo</Label>
                <Button
                  variant="outline"
                  disabled={!canWrite || form.slides.length >= 6}
                  onClick={() =>
                    set('slides', [
                      ...form.slides,
                      { key: crypto.randomUUID(), image: '', title: '', subtitle: '' },
                    ])
                  }
                >
                  <ImagePlus className="h-4 w-4" />
                  Novo banner
                </Button>
              </div>

              {form.slides.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                  Sem banner, o topo usa a chamada principal da pagina.
                </p>
              ) : null}

              <ul className="space-y-3">
                {form.slides.map((slide, index) => (
                  <li key={slide.key} className="space-y-3 rounded-xl border border-border p-4">
                    <div className="flex items-start gap-3">
                      {slide.image ? (
                        <img
                          src={slide.image}
                          alt=""
                          className="h-20 w-28 shrink-0 rounded-lg border border-border object-cover"
                        />
                      ) : null}

                      <div className="min-w-0 flex-1 space-y-2">
                        <Input
                          placeholder="URL da imagem"
                          disabled={!canWrite}
                          value={slide.image}
                          onChange={(event) =>
                            set(
                              'slides',
                              form.slides.map((item, position) =>
                                position === index ? { ...item, image: event.target.value } : item,
                              ),
                            )
                          }
                        />
                        <Input
                          placeholder="Chamada do banner"
                          disabled={!canWrite}
                          value={slide.title}
                          onChange={(event) =>
                            set(
                              'slides',
                              form.slides.map((item, position) =>
                                position === index ? { ...item, title: event.target.value } : item,
                              ),
                            )
                          }
                        />
                        <Input
                          placeholder="Linha de apoio (ex.: Promocao do meio de semana)"
                          disabled={!canWrite}
                          value={slide.subtitle}
                          onChange={(event) =>
                            set(
                              'slides',
                              form.slides.map((item, position) =>
                                position === index
                                  ? { ...item, subtitle: event.target.value }
                                  : item,
                              ),
                            )
                          }
                        />
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remover banner"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        disabled={!canWrite}
                        onClick={() =>
                          set(
                            'slides',
                            form.slides.filter((_, position) => position !== index),
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-base font-semibold">Contato</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp (com DDI)</Label>
                <Input
                  id="whatsapp"
                  type="tel"
                  placeholder="5511999999999"
                  disabled={!canWrite}
                  value={form.whatsapp}
                  onChange={(event) => set('whatsapp', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  disabled={!canWrite}
                  value={form.email}
                  onChange={(event) => set('email', event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsappMessage">Mensagem padrao do WhatsApp</Label>
              <Input
                id="whatsappMessage"
                disabled={!canWrite}
                value={form.whatsappMessage}
                onChange={(event) => set('whatsappMessage', event.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="address">Endereco</Label>
                <Input
                  id="address"
                  disabled={!canWrite}
                  value={form.address}
                  onChange={(event) => set('address', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  disabled={!canWrite}
                  value={form.city}
                  onChange={(event) => set('city', event.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  disabled={!canWrite}
                  value={form.instagramUrl}
                  onChange={(event) => set('instagramUrl', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="facebook">Facebook</Label>
                <Input
                  id="facebook"
                  disabled={!canWrite}
                  value={form.facebookUrl}
                  onChange={(event) => set('facebookUrl', event.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <div>
              <h2 className="text-base font-semibold">Servicos em destaque</h2>
              <p className="text-sm text-muted-foreground">
                Os marcados aparecem primeiro. Sem nenhum, a pagina mostra o catalogo na ordem
                normal.
              </p>
            </div>

            <ul className="divide-y divide-border rounded-lg border border-border">
              {(services.data?.data ?? []).map((service) => {
                const highlighted = form.highlightIds.includes(service.id);

                return (
                  <li key={service.id}>
                    <button
                      type="button"
                      disabled={!canWrite}
                      onClick={() => toggleHighlight(service.id)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60"
                    >
                      <Star
                        className={cn(
                          'h-5 w-5 shrink-0',
                          highlighted ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground',
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate">{service.name}</span>
                      <span className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatCurrency(service.price)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          {canWrite ? (
            <div className="flex justify-end border-t border-border pt-6">
              <Button
                className="w-full sm:w-auto"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    ...form,
                    // A chave dos banners e so do React; a API recebe o dado limpo.
                    slides: form.slides
                      .filter((slide) => slide.image.trim())
                      .map(({ key: _key, ...slide }) => slide),
                  })
                }
              >
                {save.isPending ? <Spinner /> : <Check />}
                Salvar
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
